import { ROUTES } from 'app/utils/constants/paths';
import { getLTIContentIcon, isLTI, isLemonadeQuestion, isVideolesson, roundNumber } from 'core';
import { updateLessonItem } from 'core/crud/contents.crud';
import useAmplitude from 'core/hooks/useAmplitude';
import useDynamicPath from 'core/hooks/useDynamicPath';
import { useUser } from 'core/lite/hooks/useUser';
import IconBackground from 'core/modules/atoms/IconBackground';
import ItemComplexList from 'core/modules/components/general/ItemComplexList';
import AssessmentGradeTag from 'core/modules/compositions/AssessmentGradeTag';
import AssessmentStatusTags from 'core/modules/compositions/AssessmentStatusTags';
import EditBy from 'core/modules/compositions/EditBy/EditBy';
import * as coreEntities from 'core/store/index';
import { getUTCDateWithoutTimeZone } from 'core/utils/date';
import { parseGradesToScoreScale } from 'core/utils/parses';
import { AMPLITUDE_EVENT, typeContentsIcons } from 'core/variables/constant';
import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import { withTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Button, Icon, IconBubble, Image, ListSimpleDropdown, ProgressBar, Popover, Tag } from 'ui';
import Tooltip from 'ui/atoms/Tooltip/Tooltip';

const LessonElementItem = ({
  contentLang,
  t,
  isLessonEditable,
  isItemEditable,
  isReferenced,
  type,
  name,
  guid,
  statusItem,
  consumption,
  typeComponent,
  dateProgrammed,
  onClickEdit,
  onClickEditLessonItem,
  onClickSeeTestResult,
  onClickView,
  onClickDeleteLessonItem,
  additional,
  jobStatus,
  isStudent,
  isEditorial,
  lessonGuid,
  deliveredAt,
  publishScoreAt,
  item_jobs_completed,
  evaluatedBy,
  content_is_evaluable,
  can_deliver_late,
  job_delivered_at,
  job_score,
  job_progress,
  test_amount_questions,
  freezed_amount_questions,
  contentGuid,
  lessonImage,
  isGuideline,
  showBigContent,
  isTeacherOnly,
  scoreScale,
  content_data,
  courseGuid,
  authorName,
  authorAvatar,
  contentCover,
  itemFor,
  itemForUsers,
}) => {
  const { getDynamicPath } = useDynamicPath();
  const language = useSelector((state) => coreEntities.i18n.selectors.getLanguage(state));
  const FORMAT = language === 'en' ? 'YYYY/MM/DD HH:mm' : 'DD/MM/YYYY HH:mm';
  const [anchorEl, setAnchorEl] = React.useState({});
  const [openDropDown, setOpenDropDown] = React.useState(false);
  const [status, setStatus] = React.useState(statusItem);
  const [showPreview] = React.useState(showBigContent || ['xapi'].includes(typeComponent));
  const { sendAmplitudeEvent } = useAmplitude();
  const { isUserFromGoogle, isUserFromMicrosoft } = useUser();

  function renderLeft() {
    let icon = type || 'CTTY_09';
    if (isLTI(type)) icon = getLTIContentIcon(content_data);

    return (
      <div>
        <IconBubble name={typeContentsIcons[icon]} size="lg" background="alpha" color="primary" />
      </div>
    );
  }

  function onShowDropdown(e) {
    e.preventDefault();
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setOpenDropDown(true);
  }

  function renderMoreIcon() {
    return (
      <div className="lesson-element-item__right-item " onClick={onShowDropdown}>
        <Icon name="more-bullets" />
      </div>
    );
  }
  function renderContentType() {
    if (isLTI(type)) {
      return t('lesson:LTI Content').toUpperCase();
    } else if (['scorm', 'content', 'xapi'].includes(typeComponent)) {
      return t(type).toUpperCase();
    } else if (isVideolesson(type)) {
      return `${content_is_evaluable ? t('assigments:Evaluative videolesson').toUpperCase() : t('assigments:Formative videolesson').toUpperCase()}`;
    } else if (isLemonadeQuestion(type)) {
      return `${content_is_evaluable ? t('assigments:Evaluative assignment').toUpperCase() : t('assigments:Formative assignment').toUpperCase()}`;
    } else {
      return `${content_is_evaluable ? t('assigments:Evaluative quizz').toUpperCase() : t('assigments:Formative quizz').toUpperCase()}: ${t(
        'lesson:X questions',
        { count: freezed_amount_questions || test_amount_questions }
      )}`;
    }
  }

  function renderCenter() {
    return (
      <div className="lesson-element-item__center ">
        <span className="lesson-element-item__type ">
          {!isStudent && additional ? `${t('lesson:Additional content')} - ` : ''}
          {renderContentType()}
        </span>
        <span className="lesson-element-item__name " title={name} lang={contentLang}>
          {name}
        </span>
        {authorName && !isStudent && (
          <span className="lesson-element-item__created-by">
            <EditBy name={authorName} avatar={authorAvatar} t={t} />
          </span>
        )}
      </div>
    );
  }

  function renderRight() {
    return (
      <div className="lesson-element-item__right">
        {/* {renderCount(assigned, t('lesson:Assigned'))}
        {renderCount(graded, t('lesson:Graded'))} */}
        {deliveredAt && (
          <div className="lesson-element-item__right-deliver-at">
            <IconBackground icon="calendar" text={t('assigments:Due date')} description={moment(deliveredAt).format(FORMAT)} />
          </div>
        )}
        {publishScoreAt && !isStudent && (
          <div className="lesson-element-item__right-deliver-at">
            <IconBackground icon="calendar-done" text={t('assigments:Grade publication date')} description={moment(publishScoreAt).format(FORMAT)} />
          </div>
        )}
        {status === 'published' && !isEditorial && !isGuideline && !isTeacherOnly && (
          <div className="lesson-element-item__right-item lesson-element-item__right-item--progress-bar ">
            <ProgressBar label={t('course:Consumption').toUpperCase()} labelPosition="bottom" percentagePosition="top" progress={consumption} />
          </div>
        )}
        {isTeacherOnly && (
          <div className="lesson-element-item__only-teacher">
            <Button ariaLabel={t('assigments:Teacher notes')} leftIcon="teacher" variant="neutral-soft" text="" isIconOnly={true} type={'button'} isFullWidth />
          </div>
        )}
        {status === 'draft' && !isTeacherOnly && (
          <Tooltip ariaMode="labelledby" placement="top" title={t('content:Hide to students')}>
            <div className="lesson-element-item__right-info">
              <Icon name={'hide'} />
            </div>
          </Tooltip>
        )}
        {status === 'scheduled' && (
          <div className="lesson-element-item__right-info lesson-element-item__right-info--scheduled">
            <Tag label={t('lesson:Scheduled')} color="primary" />

            <div className="lesson-element-item__right-date-container">
              <span className="lesson-element-item__right-date">{moment(dateProgrammed).format('MMM DD, HH:mm')}</span>
            </div>
          </div>
        )}
        {(isItemEditable || isLessonEditable || isReferenced) && !isGuideline && getOptions().length > 0 && renderMoreIcon()}
        {/* {['xapi'].includes(typeComponent) && renderGoToTest()} */}
      </div>
    );
  }

  function renderMeta(icon, text, description) {
    return (
      <div className="lesson-element-item__meta">
        <div className="lesson-element-item__meta-icon">
          <Icon name={icon} />
        </div>
        <div className="lesson-element-item__meta-info">
          <span className="lesson-element-item__meta-info-text">{text}</span>
          <span className="lesson-element-item__meta-info-description">{description}</span>
        </div>
      </div>
    );
  }

  function renderGoToTest() {
    const onGo = () => {
      if (typeComponent === 'assigment') {
        return onClickEdit();
      }
      if (typeComponent === 'scorm' || typeComponent === 'xapi') {
        return onClickView();
      }
    };

    return (
      <div className="lesson-element-item__go-to-test">
        <Button onClick={() => onGo()} text={t('common:Go')} rightIcon="chevron-right" iconSize="md" />
      </div>
    );
  }

  function renderGoToResultTest() {
    return (
      <div className="lesson-element-item__go-to-test">
        <Button onClick={onClickSeeTestResult} text={t('assigments:See results')} rightIcon="chevron-right" />
      </div>
    );
  }

  function renderTag(icon, text, color = 'default') {
    return <Tag color={color} label={text} rightIcon={icon} size="lg" />;
  }

  function renderDelivered() {
    // delivered + late
    // delivered
    // delivered no evaluable -> go to test button

    if (moment(deliveredAt).isBefore(job_delivered_at))
      return (
        <>
          <AssessmentStatusTags status="turned-in-late" size="xxl" /> {!content_is_evaluable && renderGoToTest()}
        </>
      );
    else
      return (
        <>
          <AssessmentStatusTags status="turned-in" size="xxl" /> {!content_is_evaluable && renderGoToTest()}
        </>
      );
  }

  function renderPending() {
    // pending late + can deliver
    // pending late + cannot deliver
    // pending in time + assigned
    // pending in time + reassigned

    if (evaluatedBy)
      return (
        <>
          <AssessmentStatusTags status="assessment-reassigned" size="xxl" />
          {renderGoToTest()}
        </>
      );
    else {
      if (moment(deliveredAt).isBefore()) {
        return (
          <>
            <AssessmentStatusTags status={can_deliver_late ? 'late' : 'timed-out'} size="xxl" /> {can_deliver_late === 1 && renderGoToTest()}
          </>
        );
      } else {
        return (
          <>
            <AssessmentStatusTags status="assigned" size="xxl" />
            {renderGoToTest()}
          </>
        );
      }
    }
  }

  function renderScormScore() {
    return job_score || job_score === 0 ? (
      <AssessmentGradeTag grade={parseGradesToScoreScale(job_score, scoreScale)} gradeTotal={parseGradesToScoreScale(10, scoreScale)} />
    ) : (
      <AssessmentStatusTags status="assigned" size="xxl" />
    );
  }

  function renderProgress() {
    let progress = 0;
    if (job_progress) {
      progress = job_progress * 100 > 100 ? 100 : job_progress * 100;
      try {
        if (progress && progress.toString().length > 2) progress = roundNumber(progress);
      } catch {
        // silent fallback
      }
    }

    return progress;
  }

  function renderCompletionProgressBar() {
    return (
      <div className="lesson-element-item__right-item">
        <div className="tag tag--color-gray tag--size-big tag--progress-bar">
          <div className="lesson-element-item__right-item--progress-bar">
            <ProgressBar hasPercentageLabel={false} isLabelHidden label={t('lesson:Progress')} progress={Math.min(job_progress * 100, 100)} />
          </div>
          <div className="progress-value" aria-hidden="true">
            <div className="progress-value__number">{renderProgress()}</div>
            <div className="progress-value__percent">%</div>
          </div>
        </div>
      </div>
    );
  }

  function renderRightStudent() {
    return (
      <div className="lesson-element-item__right">
        <div className="lesson-element-item__right-info lesson-element-item__right-info--student ">
          {['assigment'].includes(typeComponent) &&
            deliveredAt &&
            (jobStatus === 'pending' || !jobStatus) &&
            renderMeta('calendar', moment(deliveredAt).format('MMM DD, HH:mm'), t('assigments:Due date'))}
          {['assigment'].includes(typeComponent) && jobStatus === 'evaluated' && renderTag('emoji-stars', t('assigments:Grades published'))}
          {['assigment'].includes(typeComponent) && jobStatus === 'evaluated' && renderGoToResultTest()}
          {['assigment'].includes(typeComponent) && jobStatus === 'delivered' && renderDelivered()}
          {['assigment'].includes(typeComponent) && (jobStatus === 'pending' || !jobStatus) && renderPending()}
          {['scorm'].includes(typeComponent) && renderCompletionProgressBar()}
          {['scorm'].includes(typeComponent) && renderScormScore()}
          {['scorm'].includes(typeComponent) && renderGoToTest()}
          {['xapi'].includes(typeComponent) && renderCompletionProgressBar()}
          {['xapi'].includes(typeComponent) && renderScormScore()}
          {['xapi'].includes(typeComponent) && renderGoToTest()}
        </div>
      </div>
    );
  }

  function renderPreviewContent() {
    if (contentCover) {
      return (
        <div className="lesson-element-item__big-preview-content">
          <Image src={contentCover} alt={t('alt:Preview image for itemName', { itemName: name })} />
        </div>
      );
    } else if (lessonImage) {
      return (
        <div className="lesson-element-item__big-preview-content">
          <Image src={lessonImage} alt={t('alt:Preview image for itemName', { itemName: name })} />
        </div>
      );
    }
  }

  function handleCloseDropdown() {
    setOpenDropDown(false);
  }

  function onClickOption(e, callback) {
    e.preventDefault();
    e.stopPropagation();
    setOpenDropDown(false);
    callback();
  }

  async function updateLessonItemStatus() {
    if (status == 'draft') {
      const payload = {
        guid: guid,
        lesson_guid: lessonGuid,
        status: 'published',
        published_at: getUTCDateWithoutTimeZone(new Date()),
        item_for: itemFor ? itemFor : 'all',
        item_for_users: itemForUsers ? itemForUsers.split(',') : [],
        // deliver_at: moment(deliveredAt).isValid() ? getDateFormatServerInUTC(deliveredAt) : getDateFormatServerInUTC(new Date()),
        ...(isReferenced && { course_guid: courseGuid }),
      };
      await updateLessonItem(payload);
      sendAmplitudeEvent(AMPLITUDE_EVENT.SHOW_LESSON_ITEM, { status: 'published' });
      setStatus('published');
    } else {
      const payload = {
        guid: guid,
        lesson_guid: lessonGuid,
        status: 'draft',
        published_at: undefined,
        item_for: itemFor ? itemFor : 'all',
        item_for_users: itemForUsers ? itemForUsers.split(',') : [],
        ...(isReferenced && { course_guid: courseGuid }),
      };
      await updateLessonItem(payload);
      sendAmplitudeEvent(AMPLITUDE_EVENT.SHOW_LESSON_ITEM, { status: 'draft' });
      setStatus('draft');
    }
  }

  function getAssignmentLink() {
    return window.location.href + '/assessment/' + guid + '?type=embed';
  }

  function getScormLink() {
    const path = getDynamicPath(ROUTES.VIEWER_FULL_V1, { guid: contentGuid }, { lessonItem: guid });
    return window.location.origin + path;
  }

  function onClickGoogleClassroom(activityType) {
    sendAmplitudeEvent(AMPLITUDE_EVENT.SHARE_GOOGLE_CLASSROOM);
    window.open(
      `https://classroom.google.com/share?url=${activityType === 'scorm' ? getScormLink() : getAssignmentLink()}`,
      'Google Classroom Share',
      'height=500,width=640'
    );
  }

  function onClickMicrosoftTeams(activityType) {
    window.open(
      `https://teams.microsoft.com/share?href=${activityType === 'scorm' ? getScormLink() : getAssignmentLink()}`,
      'Microsoft Teams Share',
      'height=600,width=700'
    );
  }

  function getOptions() {
    const canShareSocial = typeComponent === 'assigment' || typeComponent === 'scorm';
    const canEditItem = isLessonEditable || isItemEditable || (isLessonEditable && isReferenced);
    //Las opciones avanzadas como editar y eliminar deben ser visibles en:
    // 1. Versión personalizada dentro del curso -> type: referenced && course_template_type: template_2
    // 2. Versión personalizada fuera del curso -> type: template-2 && course_template_type: template_1
    // 3. Todos los items "custom" dentro y fuera del curso (is_customized = 1)
    const canEditVisibility = isLessonEditable || isReferenced || isItemEditable;
    //La opción de publicar y despublicar debe ser visible en:
    // 1. Versión editorial dentro del curso -> type: referenced && course_template_type: template_1
    // 2. Versión personalizada dentro del curso -> type: referenced && course_template_type: template_2
    // 3. Versión personalizada fuera del curso -> type: template-2 && course_template_type: template_1
    // 4. Todos los items "custom" dentro y fuera del curso (is_customized = 1)
    const canUnpublishItem =
      (status === 'published' && typeComponent !== 'assigment') ||
      status === 'scheduled' ||
      (status === 'published' && item_jobs_completed === 0 && typeComponent === 'assigment');

    let options = [];

    if (canShareSocial) {
      if (isUserFromMicrosoft || (!isUserFromGoogle && !isUserFromMicrosoft)) {
        options.unshift({
          name: t('lesson:Microsoft Teams'),
          icon: 'teams',
          onClick: (a, e) => onClickOption(e, () => onClickMicrosoftTeams(typeComponent)),
          colorIcon: ' ',
        });
      }
      if (isUserFromGoogle || (!isUserFromGoogle && !isUserFromMicrosoft)) {
        options.unshift({
          name: t('lesson:Google Classroom'),
          icon: 'google-classroom',
          colorIcon: 'original',
          onClick: (a, e) => onClickOption(e, () => onClickGoogleClassroom(typeComponent)),
        });
      }
    }

    if (canEditItem) {
      options.unshift({ name: t('actions:Delete'), icon: 'trash', onClick: (a, e) => onClickOption(e, onClickDeleteLessonItem) });
      options.unshift({
        name: t('actions:Edit'),
        icon: 'pencil',
        onClick: (a, e) => onClickOption(e, typeComponent === 'assigment' ? onClickEdit : onClickEditLessonItem),
      });
    }

    if (canEditVisibility && !isTeacherOnly) {
      if (canUnpublishItem) {
        options.unshift({ name: t('actions:Unpublish'), icon: 'visible-off', onClick: (a, e) => onClickOption(e, updateLessonItemStatus) });
      }
      if (status === 'draft') {
        options.unshift({ name: t('content:Publish'), icon: 'send', onClick: (a, e) => onClickOption(e, updateLessonItemStatus) });
      }
    }

    return options;
  }

  return (
    <div className="lesson-element-item">
      <ItemComplexList
        left={renderLeft()}
        center={renderCenter()}
        right={isStudent ? renderRightStudent() : renderRight()}
        previewContent={showPreview ? renderPreviewContent() : null}
      />
      {/*isStudent && typeComponent === 'assigment' && (
        <>
          <div className="lesson-element-item__hr">
            <Hr />
          </div>

          <FeedBackUser
            loading={loadingComment}
            currentUser={currentUser}
            comments={comments}
            commentsLength={comments.length}
            onPublishComment={onPublishComment}
            onEditComment={onEditComment}
            onDeleteComment={onDeleteComment}
          />
        </>
      )*/}
      {openDropDown && (
        <Popover
          anchorEl={anchorEl}
          open={openDropDown}
          handleClose={handleCloseDropdown}
          className="lesson-element-item__dropdown-lib"
          shouldFocusOnOpen={false}
        >
          <ListSimpleDropdown items={getOptions()} shouldAutoFocusFirstItem />
        </Popover>
      )}
    </div>
  );
};

LessonElementItem.propTypes = {
  contentLang: PropTypes.string,
  typeComponent: PropTypes.oneOf(['content', 'assigment', 'scorm', 'xapi', 'test']),
  questionsLength: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onClickEdit: PropTypes.func,
  onClickView: PropTypes.func,
  onClickDelete: PropTypes.func,
  onClickDeleteLessonItem: PropTypes.func,
  status: PropTypes.string,
  additional: PropTypes.bool,
  t: PropTypes.func,
  isLessonEditable: PropTypes.bool,
  isItemEditable: PropTypes.bool,
  isReferenced: PropTypes.bool,
  type: PropTypes.string,
  name: PropTypes.string,
  guid: PropTypes.string,
  statusItem: PropTypes.string,
  consumption: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  dateProgrammed: PropTypes.string,
  onClickEditLessonItem: PropTypes.func,
  onClickSeeTestResult: PropTypes.func,
  jobStatus: PropTypes.string,
  isStudent: PropTypes.bool,
  isEditorial: PropTypes.bool,
  lessonGuid: PropTypes.string,
  deliveredAt: PropTypes.string,
  publishScoreAt: PropTypes.string,
  item_jobs_completed: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  evaluatedBy: PropTypes.object,
  content_is_evaluable: PropTypes.bool,
  can_deliver_late: PropTypes.bool,
  job_delivered_at: PropTypes.string,
  job_score: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  job_progress: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  test_amount_questions: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  freezed_amount_questions: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  contentGuid: PropTypes.string,
  lessonImage: PropTypes.string,
  isGuideline: PropTypes.bool,
  showBigContent: PropTypes.bool,
  isTeacherOnly: PropTypes.bool,
  scoreScale: PropTypes.object,
  content_data: PropTypes.object,
  courseGuid: PropTypes.string,
  authorName: PropTypes.string,
  authorAvatar: PropTypes.string,
  contentCover: PropTypes.string,
  itemFor: PropTypes.string,
  itemForUsers: PropTypes.string,
};

LessonElementItem.defaultProps = {
  typeComponent: 'content',
  questionsLength: 0,
  onClickEdit: function () {},
  onClickView: function () {},
  onClickDelete: function () {},
  onClickDeleteLessonItem: function () {},
  onClickSeeTestResult: function () {},
  status: 'published',
  additional: false,
};

export default withTranslation(['types-resources', 'course', 'lesson', 'assigments', 'common'])(LessonElementItem);
